import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import axios from "axios";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiTarget,
  FiActivity,
  FiCheckCircle,
  FiXCircle,
  FiPieChart,
  FiList,
  FiShield,
  FiAlertTriangle,
} from "react-icons/fi";
import { IoWalletOutline, IoStatsChartOutline } from "react-icons/io5";
import { HiOutlineChartBar } from "react-icons/hi";

const IndividualAnalysisPanel = React.memo(
  ({ show, onClose, teamAccountData }) => {
    const { user, apiBaseUrl } = useAuth();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [marginData, setMarginData] = useState(null);
    const [activeTab, setActiveTab] = useState("metrics");

    const isTeamMode = !!teamAccountData;

    useEffect(() => {
      if (show) {
        if (isTeamMode) {
          fetchTeamAnalysisData();
        } else if (user?.accounts) {
          fetchAnalysisData();
        }
      }
    }, [show, user, teamAccountData]);

    const fetchTeamAnalysisData = async () => {
      setLoading(true);
      try {
        const teamLogin = teamAccountData?.team?.accounts?.[0]?.login;

        if (!teamLogin) {
          console.error("❌ login حساب تیم یافت نشد!");
          setLoading(false);
          return;
        }

        const [leaderboardResponse, marginResponse] = await Promise.all([
          axios.get(
            `${apiBaseUrl}/api/customer/account/user_details/${teamLogin}/leaderboard`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          ),
          axios.post(
            `${apiBaseUrl}/api/user/final/accounts/margin`,
            {
              target: "groups",
              login: String(teamLogin),
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          ),
        ]);

        setData(leaderboardResponse.data);
        setMarginData(marginResponse.data?.data?.data);
      } catch (error) {
        console.error("❌ خطا در دریافت داده‌های تیم:", error);

        if (error.response) {
          console.error("❌ Response Error:", error.response.data);
          console.error("❌ Status:", error.response.status);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchAnalysisData = async () => {
      setLoading(true);
      try {
        const stage4Account = user.accounts.find(
          (acc) => acc.stage_info?.stage_number == 4
        );

        if (!stage4Account) {
          console.error("❌ حساب مرحله 4 یافت نشد!");
          setLoading(false);
          return;
        }

        const loginId = stage4Account.login;

        const [leaderboardResponse, marginResponse] = await Promise.all([
          axios.get(
            `${apiBaseUrl}/api/customer/account/user_details/${loginId}/leaderboard`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          ),
          axios.post(
            `${apiBaseUrl}/api/user/final/accounts/margin`,
            {
              target: "individuals",
              login: String(loginId),
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          ),
        ]);

        setData(leaderboardResponse.data);
        setMarginData(marginResponse.data?.data?.data);
      } catch (error) {
        console.error("❌ خطا در دریافت داده‌های آنالیز:", error);

        if (error.response) {
          console.error("❌ Response Error:", error.response.data);
          console.error("❌ Status:", error.response.status);
        }
      } finally {
        setLoading(false);
      }
    };

    if (!show) return null;

    const calculateProfit = () => {
      if (!data?.user?.balance || !marginData?.initial_account_balance)
        return 0;
      return data.user.balance - marginData.initial_account_balance;
    };

    const calculateMinDailyBalance = () => {
      if (!marginData) return 0;
      const dailyStarting = parseFloat(marginData.daily_starting_balance || 0);
      const dailyMaxLoss = parseFloat(marginData.daily_max_allowed_loss || 0);
      return dailyStarting - dailyMaxLoss;
    };

    const calculateMinOverallBalance = () => {
      if (!marginData) return 0;
      const initialBalance = parseFloat(
        marginData.initial_account_balance || 0
      );
      const overallMaxLoss = parseFloat(
        marginData.overall_max_allowed_loss || 0
      );
      return initialBalance - overallMaxLoss;
    };

    const equityChartData =
      data?.equity_balance?.data?.map((item) => ({
        date: item["on-date"],
        Balance: item.balance,
        Equity: item.equity,
      })) || [];

    const symbolDistribution =
      data?.positions?.reduce((acc, pos) => {
        const existing = acc.find((item) => item.name === pos.symbol);
        if (existing) {
          existing.value += 1;
        } else {
          acc.push({ name: pos.symbol, value: 1 });
        }
        return acc;
      }, []) || [];

    const COLORS = [
      "#fd0d0e",
      "#f59e0b",
      "#10b981",
      "#3b82f6",
      "#8b5cf6",
      "#ec4899",
    ];

    const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        return (
          <div
            className="px-4 py-3 rounded-xl shadow-2xl border backdrop-blur-xl"
            style={{
              background:
                "linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)",
              borderColor: "rgba(253, 13, 14, 0.3)",
            }}
          >
            <p className="text-white font-bold mb-2 text-sm">{label}</p>
            {payload.map((entry, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-3"
              >
                <span className="text-xs text-gray-400">{entry.name}:</span>
                <span
                  className="text-sm font-bold"
                  style={{ color: entry.color }}
                >
                  ${parseFloat(entry.value).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        );
      }
      return null;
    };

    const CustomPieTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        return (
          <div
            className="px-4 py-2 rounded-xl shadow-2xl border backdrop-blur-xl"
            style={{
              background:
                "linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.95) 100%)",
              borderColor: "rgba(253, 13, 14, 0.3)",
            }}
          >
            <p className="text-white font-bold text-sm">{payload[0].name}</p>
            <p className="text-xs text-gray-400">
              معاملات:{" "}
              <span className="text-red-400 font-bold">{payload[0].value}</span>
            </p>
          </div>
        );
      }
      return null;
    };

    const renderMetrics = () => (
      <div className="space-y-6">
        {/* Main Stats */}
        <div className="grid grid-cols-4 gap-5">
          {[
            {
              icon: <IoWalletOutline size={22} />,
              label: "موجودی",
              value: `$${parseFloat(
                data?.user?.balance || 0
              ).toLocaleString()}`,
              subLabel: "Balance",
              color: "#fd0d0e",
              bgColor: "rgba(253, 13, 14, 0.1)",
            },
            {
              icon:
                calculateProfit() >= 0 ? (
                  <FiTrendingUp size={22} />
                ) : (
                  <FiTrendingDown size={22} />
                ),
              label: "سود/ضرر",
              value: `$${Math.abs(calculateProfit()).toLocaleString()}`,
              subLabel: calculateProfit() >= 0 ? "Profit" : "Loss",
              color: calculateProfit() >= 0 ? "#10b981" : "#ef4444",
              bgColor:
                calculateProfit() >= 0
                  ? "rgba(16, 185, 129, 0.1)"
                  : "rgba(239, 68, 68, 0.1)",
            },
            {
              icon: <FiTarget size={22} />,
              label: "نرخ برد",
              value: `${data?.metrics?.win_rate || 0}%`,
              subLabel: "Win Rate",
              color: "#f59e0b",
              bgColor: "rgba(245, 158, 11, 0.1)",
            },
            {
              icon: <FiActivity size={22} />,
              label: "کل معاملات",
              value: data?.metrics?.total_trades || 0,
              subLabel: "Total Trades",
              color: "#8b5cf6",
              bgColor: "rgba(139, 92, 246, 0.1)",
            },
          ].map((stat, index) => (
            <div
              key={index}
              className="rounded-2xl p-6 border backdrop-blur-sm transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: "rgba(17, 24, 39, 0.6)",
                borderColor: "rgba(55, 65, 81, 0.4)",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: stat.bgColor }}
                >
                  <span style={{ color: stat.color }}>{stat.icon}</span>
                </div>
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: stat.color }}
                />
              </div>
              <p className="text-gray-400 text-sm mb-2">{stat.label}</p>
              <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
              <p className="text-xs" style={{ color: stat.color }}>
                {stat.subLabel}
              </p>
            </div>
          ))}
        </div>

        {/* Margin Analysis */}
        {marginData && (
          <div
            className="rounded-2xl p-6 border backdrop-blur-sm"
            style={{
              backgroundColor: "rgba(17, 24, 39, 0.6)",
              borderColor: "rgba(253, 13, 14, 0.3)",
              boxShadow: "0 4px 20px rgba(253, 13, 14, 0.1)",
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: "rgba(253, 13, 14, 0.2)" }}
              >
                <FiShield size={24} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">تحلیل مارجین</h3>
                <p className="text-xs text-gray-400">Margin Analysis</p>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-4">
              {[
                {
                  icon: <IoWalletOutline size={18} />,
                  label: "بالانس فعلی",
                  value: `$${parseFloat(
                    marginData.current_account_balance || 0
                  ).toLocaleString()}`,
                  subLabel: "Current Balance",
                  color: "#3b82f6",
                },
                {
                  icon: <FiAlertTriangle size={18} />,
                  label: "ضرر مجاز روزانه",
                  value: `$${parseFloat(
                    marginData.daily_max_allowed_loss || 0
                  ).toLocaleString()}`,
                  subLabel: "Daily Max Loss",
                  color: "#f59e0b",
                },
                {
                  icon: <FiTrendingDown size={18} />,
                  label: "ضرر مجاز کل",
                  value: `$${parseFloat(
                    marginData.overall_max_allowed_loss || 0
                  ).toLocaleString()}`,
                  subLabel: "Overall Max Loss",
                  color: "#ef4444",
                },
                {
                  icon: <FiShield size={18} />,
                  label: "کمترین بالانس روز",
                  value: `$${calculateMinDailyBalance().toLocaleString()}`,
                  subLabel: "Min Daily Balance",
                  color: "#8b5cf6",
                },
                {
                  icon: <FiCheckCircle size={18} />,
                  label: "کمترین بالانس کل",
                  value: `$${calculateMinOverallBalance().toLocaleString()}`,
                  subLabel: "Min Overall Balance",
                  color: "#10b981",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="rounded-xl p-4 border backdrop-blur-sm"
                  style={{
                    backgroundColor: "rgba(31, 41, 55, 0.5)",
                    borderColor: `${item.color}30`,
                  }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${item.color}20` }}
                  >
                    <span style={{ color: item.color }}>{item.icon}</span>
                  </div>
                  <p className="text-gray-400 text-xs mb-1">{item.label}</p>
                  <p className="text-lg font-bold text-white mb-1">
                    {item.value}
                  </p>
                  <p className="text-xs" style={{ color: item.color }}>
                    {item.subLabel}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance */}
        <div
          className="rounded-2xl p-6 border backdrop-blur-sm"
          style={{
            backgroundColor: "rgba(17, 24, 39, 0.6)",
            borderColor: "rgba(55, 65, 81, 0.4)",
          }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "rgba(253, 13, 14, 0.2)" }}
            >
              <HiOutlineChartBar size={22} className="text-red-400" />
            </div>
            <h3 className="text-lg font-bold text-white">عملکرد معاملاتی</h3>
          </div>

          <div className="grid grid-cols-3 gap-4">
            {[
              {
                icon: <FiCheckCircle size={20} />,
                label: "معاملات برنده",
                value: data?.metrics?.winning_trades || 0,
                subLabel: `سود: $${parseFloat(
                  data?.metrics?.gross_profit || 0
                ).toLocaleString()}`,
                color: "#10b981",
              },
              {
                icon: <FiXCircle size={20} />,
                label: "معاملات بازنده",
                value: data?.metrics?.losing_trades || 0,
                subLabel: `ضرر: $${parseFloat(
                  data?.metrics?.gross_loss || 0
                ).toLocaleString()}`,
                color: "#ef4444",
              },
              {
                icon: <FiTrendingDown size={20} />,
                label: "نرخ باخت",
                value: `${data?.metrics?.loss_rate || 0}%`,
                subLabel: "Loss Rate",
                color: "#f59e0b",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="rounded-xl p-5 border backdrop-blur-sm"
                style={{
                  backgroundColor: "rgba(31, 41, 55, 0.5)",
                  borderColor: "rgba(55, 65, 81, 0.3)",
                }}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: `${item.color}20` }}
                >
                  <span style={{ color: item.color }}>{item.icon}</span>
                </div>
                <p className="text-gray-400 text-xs mb-1">{item.label}</p>
                <p className="text-xl font-bold text-white mb-1">
                  {item.value}
                </p>
                <p className="text-xs text-gray-500">{item.subLabel}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    const renderChartsAndPositions = () => (
      <div className="space-y-6">
        {/* Charts */}
        <div className="grid grid-cols-2 gap-5">
          <div
            className="rounded-2xl p-6 border backdrop-blur-sm"
            style={{
              backgroundColor: "rgba(17, 24, 39, 0.6)",
              borderColor: "rgba(55, 65, 81, 0.4)",
            }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: "rgba(253, 13, 14, 0.2)" }}
              >
                <FiTrendingUp size={22} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">
                  نمودار موجودی
                </h3>
                <p className="text-xs text-gray-400">Balance & Equity</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={equityChartData}>
                <defs>
                  <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fd0d0e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#fd0d0e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(75, 85, 99, 0.2)"
                />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  style={{ fontSize: "11px" }}
                  tick={{ fill: "#9ca3af" }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: "11px" }}
                  tick={{ fill: "#9ca3af" }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ paddingTop: "20px" }} iconType="line" />
                <Line
                  type="monotone"
                  dataKey="Balance"
                  stroke="#fd0d0e"
                  strokeWidth={3}
                  dot={false}
                  fill="url(#colorBalance)"
                />
                <Line
                  type="monotone"
                  dataKey="Equity"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={false}
                  fill="url(#colorEquity)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {symbolDistribution.length > 0 && (
            <div
              className="rounded-2xl p-6 border backdrop-blur-sm"
              style={{
                backgroundColor: "rgba(17, 24, 39, 0.6)",
                borderColor: "rgba(55, 65, 81, 0.4)",
              }}
            >
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: "rgba(253, 13, 14, 0.2)" }}
                >
                  <FiPieChart size={22} className="text-red-400" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">
                    توزیع نمادها
                  </h3>
                  <p className="text-xs text-gray-400">Symbol Distribution</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={symbolDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={90}
                    dataKey="value"
                    animationDuration={800}
                  >
                    {symbolDistribution.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        stroke={COLORS[index % COLORS.length]}
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Positions Table */}
        <div
          className="rounded-2xl border backdrop-blur-sm overflow-hidden"
          style={{
            backgroundColor: "rgba(17, 24, 39, 0.6)",
            borderColor: "rgba(55, 65, 81, 0.4)",
          }}
        >
          <div
            className="p-6 border-b"
            style={{ borderColor: "rgba(55, 65, 81, 0.3)" }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: "rgba(253, 13, 14, 0.2)" }}
              >
                <FiList size={22} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">
                  لیست پوزیشن‌ها
                </h3>
                <p className="text-xs text-gray-400">تاریخچه معاملات</p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: "rgba(31, 41, 55, 0.5)" }}>
                  <th className="py-4 px-5 text-right text-xs text-gray-400 font-medium">
                    نماد
                  </th>
                  <th className="py-4 px-5 text-right text-xs text-gray-400 font-medium">
                    وضعیت
                  </th>
                  <th className="py-4 px-5 text-right text-xs text-gray-400 font-medium">
                    حجم
                  </th>
                  <th className="py-4 px-5 text-right text-xs text-gray-400 font-medium">
                    ورود
                  </th>
                  <th className="py-4 px-5 text-right text-xs text-gray-400 font-medium">
                    خروج
                  </th>
                  <th className="py-4 px-5 text-right text-xs text-gray-400 font-medium">
                    سود/ضرر
                  </th>
                  <th className="py-4 px-5 text-right text-xs text-gray-400 font-medium">
                    درصد
                  </th>
                  <th className="py-4 px-5 text-right text-xs text-gray-400 font-medium">
                    مدت
                  </th>
                </tr>
              </thead>
              <tbody>
                {data?.positions?.map((position, index) => (
                  <tr
                    key={position.position_id}
                    className="border-t transition-colors hover:bg-opacity-70"
                    style={{
                      backgroundColor:
                        index % 2 === 0
                          ? "rgba(17, 24, 39, 0.3)"
                          : "rgba(31, 41, 55, 0.3)",
                      borderColor: "rgba(55, 65, 81, 0.2)",
                    }}
                  >
                    <td className="py-4 px-5">
                      <span
                        className="text-white font-medium text-xs px-3 py-1.5 rounded-lg"
                        style={{ backgroundColor: "rgba(253, 13, 14, 0.2)" }}
                      >
                        {position.symbol}
                      </span>
                    </td>
                    <td className="py-4 px-5">
                      <span
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                          position.status === "Closed"
                            ? "text-red-400 border-red-500/30"
                            : "text-green-400 border-green-500/30"
                        }`}
                        style={{
                          backgroundColor:
                            position.status === "Closed"
                              ? "rgba(239, 68, 68, 0.1)"
                              : "rgba(16, 185, 129, 0.1)",
                        }}
                      >
                        {position.status}
                      </span>
                    </td>
                    <td className="py-4 px-5 text-gray-300 text-xs">
                      {position.volume.toLocaleString()}
                    </td>
                    <td className="py-4 px-5 text-gray-300 text-xs">
                      {position.entry_price}
                    </td>
                    <td className="py-4 px-5 text-gray-300 text-xs">
                      {position.exit_price}
                    </td>
                    <td className="py-4 px-5">
                      <span
                        className={`text-xs font-bold ${
                          position.total_profit >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        ${position.total_profit.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-4 px-5">
                      <span
                        className={`text-xs font-bold ${
                          position.profit_percentage >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {position.profit_percentage.toFixed(2)}%
                      </span>
                    </td>
                    <td className="py-4 px-5 text-gray-400 text-xs">
                      {position.time_held}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );

    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-md z-40"
          onClick={onClose}
        />

        {/* Panel */}
        <div
          className="fixed top-0 right-0 h-full z-50 flex flex-col"
          style={{
            width: "90%",
            backgroundColor: "#1f2937",
          }}
        >
          {/* Background Effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute rounded-full opacity-5 blur-3xl animate-pulse"
              style={{
                width: "600px",
                height: "600px",
                background:
                  "radial-gradient(circle, #fd0d0e 0%, transparent 70%)",
                top: "-200px",
                right: "-100px",
                animationDuration: "8s",
              }}
            />
            <div
              className="absolute rounded-full opacity-5 blur-3xl animate-pulse"
              style={{
                width: "500px",
                height: "500px",
                background:
                  "radial-gradient(circle, #fd0d0e 0%, transparent 70%)",
                bottom: "-150px",
                left: "-50px",
                animationDuration: "10s",
                animationDelay: "2s",
              }}
            />
          </div>

          {/* Header */}
          <header
            className="shadow-lg border-b backdrop-blur-xl z-20 relative"
            style={{
              backgroundColor: "rgba(17, 24, 39, 0.8)",
              borderColor: "rgba(55, 65, 81, 0.5)",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div className="px-8 py-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center transform transition-transform hover:scale-110"
                  style={{
                    backgroundColor: "#fd0d0e",
                    boxShadow: "0 4px 15px rgba(253, 13, 14, 0.3)",
                  }}
                >
                  <IoStatsChartOutline size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {isTeamMode
                      ? "پنل آنالیز حساب گروهی"
                      : "پنل آنالیز حساب فردی"}
                  </h2>
                  <p className="text-xs text-gray-400">
                    {isTeamMode
                      ? "تحلیل حرفه‌ای عملکرد تیمی"
                      : "تحلیل حرفه‌ای عملکرد معاملاتی"}
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm text-gray-300 hover:text-white rounded-xl transition-all duration-300 flex items-center gap-2 backdrop-blur-sm border group"
                style={{
                  backgroundColor: "rgba(31, 41, 55, 0.5)",
                  borderColor: "rgba(55, 65, 81, 0.5)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(220, 38, 38, 0.2)";
                  e.currentTarget.style.borderColor = "rgba(220, 38, 38, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(31, 41, 55, 0.5)";
                  e.currentTarget.style.borderColor = "rgba(55, 65, 81, 0.5)";
                }}
              >
                <span className="font-medium">بستن</span>
                <FiXCircle size={18} />
              </button>
            </div>
          </header>

          {/* Tab Switcher */}
          <div
            className="flex items-center justify-center py-5 relative z-10"
            style={{ backgroundColor: "rgba(17, 24, 39, 0.5)" }}
          >
            <div
              className="relative rounded-full p-1 backdrop-blur-sm border"
              style={{
                backgroundColor: "rgba(31, 41, 55, 0.5)",
                borderColor: "rgba(55, 65, 81, 0.5)",
              }}
            >
              <div className="flex gap-2">
                {[
                  {
                    id: "metrics",
                    label: "شاخص‌ها",
                    icon: <HiOutlineChartBar size={18} />,
                  },
                  {
                    id: "charts",
                    label: "نمودار و پوزیشن",
                    icon: <FiPieChart size={18} />,
                  },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="relative px-8 py-3 rounded-full text-sm font-bold transition-all flex items-center gap-2"
                    style={{
                      color: activeTab === tab.id ? "#ffffff" : "#9ca3af",
                      backgroundColor:
                        activeTab === tab.id ? "#fd0d0e" : "transparent",
                      boxShadow:
                        activeTab === tab.id
                          ? "0 4px 15px rgba(253, 13, 14, 0.3)"
                          : "none",
                    }}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 relative z-10">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div
                    className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center animate-spin relative"
                    style={{
                      backgroundColor: "rgba(253, 13, 14, 0.2)",
                      boxShadow: "0 0 40px rgba(253, 13, 14, 0.3)",
                    }}
                  >
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "#1f2937" }}
                    >
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: "#fd0d0e" }}
                      />
                    </div>
                  </div>
                  <p className="text-white text-lg font-bold">
                    در حال بارگذاری...
                  </p>
                  <p className="text-gray-400 text-sm mt-2">لطفاً صبر کنید</p>
                </div>
              </div>
            ) : data ? (
              <AnimatePresence mode="wait">
                {activeTab === "metrics" && renderMetrics()}
                {activeTab === "charts" && renderChartsAndPositions()}
              </AnimatePresence>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div
                    className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                    style={{
                      backgroundColor: "rgba(220, 38, 38, 0.2)",
                      boxShadow: "0 0 40px rgba(220, 38, 38, 0.3)",
                    }}
                  >
                    <FiXCircle size={40} className="text-red-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    داده‌ای یافت نشد
                  </h3>
                  <p className="text-gray-400">حساب معاملاتی یافت نشد</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
);

IndividualAnalysisPanel.displayName = "IndividualAnalysisPanel";

export default IndividualAnalysisPanel;
